import React, { useState } from 'react';
import {
  Button,
  Card,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Switch,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useAuth } from '../../auth/AuthContext';
import { useProperty } from '../../app/PropertyContext';
import { auth as authApi, getStored, setStored } from '../../lib/api';
import { useT } from '../../i18n';
import { TIMEZONE_OPTIONS } from '../settings/settingsApi';
import { LANGUAGE_SELECT_OPTIONS, normalizeLanguage } from '../onboarding/languages';
import SignatureEditor from './SignatureEditor';
import DelegationCard from './DelegationCard';
import PasskeysCard from './PasskeysCard';
import SessionsCard from './SessionsCard';
import './account.css';

export default function AccountPage() {
  const { user, updateUser } = useAuth();
  const { hasPermission } = useProperty();
  const toast = useToast();
  const t = useT();

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [jobTitle, setJobTitle] = useState(user?.jobTitle || '');
  const [timezone, setTimezone] = useState(user?.timezone || 'UTC');
  const [language, setLanguage] = useState(normalizeLanguage(user?.language));
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSig, setSavingSig] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);

  const [email, setEmail] = useState(user?.email || '');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [curPassword, setCurPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email;

  const saveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error(t('First and last name are required.'));
      return;
    }
    setSavingProfile(true);
    try {
      const next = await authApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        jobTitle: jobTitle.trim() || null,
        timezone,
        language,
      });
      updateUser(next);
      toast.success(t('Profile updated'));
    } catch (err) {
      toast.error(t('Could not update profile'), { description: err?.message });
    } finally {
      setSavingProfile(false);
    }
  };

  // Password/email changes sign every other session out and hand back a fresh token
  // pair for this one — swap it into storage so the user never notices.
  const adoptSession = (result) => {
    setStored({
      ...getStored(),
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt: result.expiresAt,
      user: result.user,
    });
    updateUser(result.user);
  };

  const saveEmail = async () => {
    const next = email.trim().toLowerCase();
    if (!next) { toast.error(t('Enter the new email address.')); return; }
    if (!emailPassword) { toast.error(t('Enter your current password to confirm.')); return; }
    setSavingEmail(true);
    try {
      adoptSession(await authApi.changeEmail(next, emailPassword));
      setEmailPassword('');
      toast.success(t('Sign-in email updated'), { description: t('Use the new email the next time you sign in.') });
    } catch (err) {
      toast.error(t('Could not update email'), { description: err?.message });
    } finally {
      setSavingEmail(false);
    }
  };

  const savePassword = async () => {
    if (!curPassword) { toast.error(t('Enter your current password.')); return; }
    if (newPassword.length < 8) { toast.error(t('The new password must be at least 8 characters.')); return; }
    if (newPassword !== confirmPassword) { toast.error(t('The new passwords do not match.')); return; }
    setSavingPassword(true);
    try {
      adoptSession(await authApi.changePassword(curPassword, newPassword));
      setCurPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(t('Password changed'), { description: t('Any other signed-in devices have been signed out.') });
    } catch (err) {
      toast.error(t('Could not change password'), { description: err?.message });
    } finally {
      setSavingPassword(false);
    }
  };

  const saveSignature = async (dataUrl) => {
    setSavingSig(true);
    try {
      const next = await authApi.updateSignature(dataUrl);
      updateUser(next);
      toast.success(t('Signature saved'));
    } catch (err) {
      toast.error(t('Could not save signature'), { description: err?.message });
    } finally {
      setSavingSig(false);
    }
  };

  const saveNotifications = async (approvalEmailsOptOut, commentEmailsOptOut, successMessage) => {
    setSavingNotify(true);
    try {
      const next = await authApi.updateNotifications(approvalEmailsOptOut, commentEmailsOptOut);
      updateUser(next);
      toast.success(successMessage);
    } catch (err) {
      toast.error(t('Could not update notification preferences'), { description: err?.message });
    } finally {
      setSavingNotify(false);
    }
  };

  const toggleApprovalEmails = (optedIn) =>
    saveNotifications(!optedIn, !!user?.commentEmailsOptOut,
      optedIn ? t('Approval emails enabled') : t('Approval emails turned off'));

  const toggleCommentEmails = (optedIn) =>
    saveNotifications(!!user?.approvalEmailsOptOut, !optedIn,
      optedIn ? t('Comment emails enabled') : t('Comment emails turned off'));

  const removeSignature = async () => {
    setSavingSig(true);
    try {
      const next = await authApi.updateSignature(null);
      updateUser(next);
      toast.success(t('Signature removed'));
    } catch (err) {
      toast.error(t('Could not remove signature'), { description: err?.message });
    } finally {
      setSavingSig(false);
    }
  };

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('My account')}
        description={t('Manage your profile and the signature stamped on documents you submit and approve.')}
        icon={<Icon.User size={22} />}
      />

      <div className="acct-grid">
        <Card>
          <Card.Header><Card.Title>{t('Profile')}</Card.Title></Card.Header>
          <Card.Body>
            <div className="acct-form">
              <div className="acct-form__row">
                <Input label={t('First name')} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                <Input label={t('Last name')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <Input label={t('Job title')} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder={t('e.g. F&B Manager')} />
              <div className="acct-form__row">
                <div className="ps-field">
                  <span className="ps-field__label">{t('Timezone')}</span>
                  <Select value={timezone} onChange={setTimezone} options={TIMEZONE_OPTIONS} ariaLabel={t('Timezone')} />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('Language')}</span>
                  <Select value={language} onChange={setLanguage} options={LANGUAGE_SELECT_OPTIONS} ariaLabel={t('Language')} />
                </div>
              </div>
              <div className="acct-form__meta">
                <span className="acct-form__muted">{t('Signed in as')}</span>
                <span>{user?.email}</span>
              </div>
              <div className="acct-form__actions">
                <Button variant="primary" loading={savingProfile} onClick={saveProfile}>{t('Save profile')}</Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Sign-in & security')}</Card.Title>
            <Card.Subtitle>{t('Change the email and password you sign in with.')}</Card.Subtitle>
          </Card.Header>
          <Card.Body>
            <div className="acct-form">
              <Input
                label={t('Sign-in email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <Input
                label={t('Current password (to confirm)')}
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="acct-form__actions">
                <Button
                  variant="secondary"
                  loading={savingEmail}
                  disabled={!email.trim() || email.trim().toLowerCase() === (user?.email || '')}
                  onClick={saveEmail}
                >
                  {t('Update email')}
                </Button>
              </div>

              <hr className="acct-divider" />

              <Input
                label={t('Current password')}
                type="password"
                value={curPassword}
                onChange={(e) => setCurPassword(e.target.value)}
                autoComplete="current-password"
              />
              <div className="acct-form__row">
                <Input
                  label={t('New password')}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  hint={t('At least 8 characters.')}
                />
                <Input
                  label={t('Confirm new password')}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="acct-form__actions">
                <Button
                  variant="secondary"
                  loading={savingPassword}
                  disabled={!curPassword || !newPassword || !confirmPassword}
                  onClick={savePassword}
                >
                  {t('Change password')}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Signature')}</Card.Title>
            <Card.Subtitle>{t('Applied automatically when you submit or approve documents.')}</Card.Subtitle>
          </Card.Header>
          <Card.Body>
            {user?.signatureImage ? (
              <div className="acct-sig__current">
                <div className="acct-sig__current-head">
                  <span className="acct-sig__current-label">{t('Current signature')}</span>
                  {user?.signatureUpdatedAt && (
                    <span className="acct-form__muted">{t('Updated {date}', { date: new Date(user.signatureUpdatedAt).toLocaleDateString() })}</span>
                  )}
                </div>
                <div className="acct-sig__preview">
                  <img src={user.signatureImage} alt={t('Your signature')} />
                  <span className="acct-sig__preview-name">{fullName}</span>
                </div>
              </div>
            ) : (
              <EmptyState
                icon={<Icon.Pencil size={26} />}
                title={t('No signature yet')}
                description={t('Create one below — draw it, type it, or upload an image.')}
              />
            )}

            <SignatureEditor
              initialName={fullName}
              saving={savingSig}
              onSave={saveSignature}
              onClear={user?.signatureImage ? removeSignature : undefined}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Header>
            <Card.Title>{t('Notifications')}</Card.Title>
            <Card.Subtitle>{t('Control the emails you receive.')}</Card.Subtitle>
          </Card.Header>
          <Card.Body>
            <div className="acct-notify">
              <Switch
                checked={!user?.approvalEmailsOptOut}
                disabled={savingNotify}
                onChange={toggleApprovalEmails}
                label={t('Approval emails')}
              />
              <p className="acct-form__muted">
                {t("When on, you'll be emailed when a document needs your approval, when reminders or escalations fire, and when a document you raised is approved or rejected.")}
              </p>
            </div>
            <div className="acct-notify" style={{ marginTop: 'var(--space-4)' }}>
              <Switch
                checked={!user?.commentEmailsOptOut}
                disabled={savingNotify}
                onChange={toggleCommentEmails}
                label={t('Comment emails')}
              />
              <p className="acct-form__muted">
                {t("When on, you'll be emailed when someone comments on a document you raised or mentions you with @. Bell notifications in the app always stay on.")}
              </p>
            </div>
          </Card.Body>
        </Card>

        <PasskeysCard />

        <SessionsCard />

        {hasPermission('approvals.delegate') && <DelegationCard />}
      </div>
    </PageContainer>
  );
}
